import { computed, makeObservable, observable } from "mobx"

import type { IEventBus } from "@/domains/events"

import type { IEpochRepository, IEpochStore } from "./interfaces"
import { EpochStore } from "./epoch-store"

export class EpochRepository implements IEpochRepository {
  epochs = new Map<number, EpochStore>()

  constructor(private readonly eventBus: IEventBus) {
    makeObservable(this, {
      epochs: observable,
      currentEpoch: computed,
    })
  }

  get currentEpoch(): IEpochStore | undefined {
    return this.getCurrent()
  }

  find(epochNumber: number): IEpochStore | undefined {
    return this.epochs.get(epochNumber)
  }

  add(epoch: IEpochStore): void {
    const existing = this.find(epoch.epochNumber)
    if (existing) {
      // Update existing epoch if it's the same epoch number
      if (existing instanceof EpochStore) {
        existing.updateData({
          epochNumber: epoch.epochNumber,
          dists: epoch.dists,
          weekly: epoch.weekly,
          mints: epoch.mints,
          gaugesMint: epoch.gaugesMint,
          mintBlockNumber: epoch.mintBlockNumber,
          mintBlockTimestamp: epoch.mintBlockTimestamp,
          recycled: epoch.recycled,
          pool2Mint: epoch.pool2Mint,
          teamMint: epoch.teamMint,
          recycledPercentage: epoch.recycledPercentage,
          freshPercentage: epoch.freshPercentage,
          circulatingEmission: epoch.circulatingEmission,
          circulatingSupply: epoch.circulatingSupply,
        })
      }
      return
    }

    this.epochs.set(epoch.epochNumber, epoch as EpochStore)
  }

  getAll(): IEpochStore[] {
    return Array.from(this.epochs.values())
  }

  getCurrent(): IEpochStore | undefined {
    if (this.epochs.size === 0) {
      return undefined
    }

    // Return the epoch with the highest epochNumber
    let maxEpochNumber = -1
    let currentEpoch: EpochStore | undefined

    for (const epoch of this.epochs.values()) {
      if (epoch.epochNumber > maxEpochNumber) {
        maxEpochNumber = epoch.epochNumber
        currentEpoch = epoch
      }
    }

    return currentEpoch
  }

  getEpochsFrom(minEpochNumber: number): IEpochStore[] {
    return this.getAll().filter((epoch) => epoch.epochNumber >= minEpochNumber)
  }

  clearHistoric(): void {
    const currentEpoch = this.getCurrent()
    if (!currentEpoch) {
      // If no current epoch, clear everything
      this.epochs.clear()
      return
    }

    // Keep only the current epoch (highest epochNumber)
    const currentEpochNumber = currentEpoch.epochNumber
    const epochsToRemove: number[] = []

    for (const [epochNumber] of this.epochs.entries()) {
      if (epochNumber !== currentEpochNumber) {
        epochsToRemove.push(epochNumber)
      }
    }

    for (const epochNumber of epochsToRemove) {
      this.epochs.delete(epochNumber)
    }
  }
}
